/**
 * 이상한 방 - 라운드 결과 화면 (순수 DOM)
 *
 * 근거 문서: 컨셉시트 8절(**"한 판 더 하자는 말이 나오는가"** 단일 지표) · 4.5절(점수) · 5절(리빌)
 *
 * ## 이 화면의 진짜 목적
 *
 * 점수를 보여 주는 것이 아니다. 이 프로젝트에는 판정 지표가 하나뿐이고(컨셉시트 8절),
 * 그것을 관측하는 **측정 장치가 "한 판 더" 버튼**이다. 그래서 레이아웃의 모든 결정이
 * "그 버튼에 얼마나 쉽게 닿는가"로 환원된다 - 화면에서 가장 크고, 카드 하단에 고정되어
 * 놓친 변화가 12개든 0개든 항상 같은 자리에 있다.
 *
 * 나머지 정보(점수·놓친 목록)의 역할도 같다. "저걸 못 봤다고???"(컨셉시트 5절)를
 * 글자로라도 남겨서 **다음 판을 누를 이유**를 만드는 것이지, 성적표를 주려는 것이 아니다.
 * 그래서 감점 항목을 늘어놓지 않는다 - 억울함은 "한 판 더"를 죽인다.
 *
 * ## "이 판 링크"를 걷어냈다 (2026-09-02)
 *
 * 결과 화면 맨 아래에 이 판으로 가는 주소와 복사 버튼이 있었다. 같은 방을 겨루라고 만든
 * 시드 공유였는데, 개발자의 판단은 **"왜 필요한거야? 없었으면 좋겠는데"**였고 그게 맞다.
 * 겨룰 상대를 부르는 경로가 아직 없어서 그 줄은 한 번도 쓰이지 않았고, 대신 이 화면의
 * 유일한 목적(= "한 판 더" 버튼, 컨셉시트 8절)으로 가는 길에 **읽어야 할 것 하나**를 더 놨다.
 *
 * 사라진 것은 **표시뿐이다.** 시드는 게임이 계속 쓰고(`ResultView.seed`도 남아 있다),
 * 주소창은 `main.ts`가 판마다 그대로 갈아 끼우므로 지금 이 판의 주소는 여전히 주소창에 있다 -
 * 버그 제보에 필요한 값은 거기서 그대로 복사된다. 다시 넣을 일이 생기면 그때는 공유할 상대가
 * 생겼을 때이고, 그 화면은 결과 화면이 아니라 방을 만드는 화면일 것이다.
 *
 * ## 버튼은 **무엇이 달라지는지**를 말해야 한다
 *
 * 직전까지 재도전 버튼은 "다시 하기"였고, 실제로도 같은 방에 **같은 변화**가 나왔다.
 * 시드 분리 이후 그 버튼은 같은 방에 **다른 변화**를 준다. 이름을 그대로 두면 말이 거짓이 되고,
 * 리빌에서 정답을 본 사람이 "어차피 아까 그거"라고 판단해 버튼을 누르지 않는다 -
 * 이 프로젝트의 유일한 지표(8절)를 문구 하나로 깎는 셈이다.
 *
 * | 맥락 | 큰 버튼 | 부가 |
 * |---|---|---|
 * | 자유 모드 | 한 판 더(새 방) | 같은 방, 다른 곳 |
 * | 단계 · 클리어 | **다음 단계** | 다른 곳으로 다시, 단계 고르기 |
 * | 단계 · 실패 | **다른 곳으로 다시** | 단계 고르기 |
 *
 * 자유 모드에서 "한 판 더"가 여전히 새 방인 이유: 자유 모드는 방을 외우는 곳이 아니라
 * 방을 갈아 가며 수치를 보는 곳이다. 단계에서 방이 고정인 이유와 정확히 대칭이다.
 *
 * 화면을 두 벌로 나누지 않은 이유: 나누면 점수와 놓친 목록이 두 곳에 생기고
 * 반드시 한쪽만 고쳐진다. 바뀌는 것은 **버튼의 말과 헤드라인**뿐이므로 얼굴만 갈아 끼운다.
 *
 * 실패 문구는 전부 **격려 톤**이다. 튜토리얼에서 막히면 멀티에 도달하지 못하는데
 * (상태모델 9.5절), 성적표는 막힌 사람을 한 번 더 밀어내는 일만 한다.
 * 그래서 실패 화면이 말해야 하는 것은 "몇 점이었다"가 아니라 **"몇 개만 더 찾으면 된다"**이다.
 *
 * ## 상태를 갖지 않는다
 *
 * 이 화면은 표시와 클릭 전달만 한다. 재시작의 시드 결정도, 점수 계산도, 클리어 판정도
 * 전부 바깥의 몫이다(판정은 `levels.meetsClearCondition` 하나뿐이어야 한다).
 * 여기서 새 시드를 만들면 `Math.random()`이 이 파일로 들어오고, 그 순간 결정론이 깨진다.
 *
 * ## 문구는 하나도 이 파일에 없다
 *
 * 모든 표시 문구는 `i18n`의 `t()`에서 나온다. 표를 들고 있던 자리(끝난 이유·동사·헤드라인)는
 * 이제 **사전 키의 표**를 들고 있다 - 값은 사전에, 고르는 규칙만 여기에 남긴다.
 * 항목을 잇는 것은 언제나 `common.separator`다. 가운뎃점은 용어집이 금지한다
 * (`라운드 결과 · 시간 초과 · 2회차`는 개발자 로그처럼 읽힌다).
 */

import { prefersReducedMotion } from '../core/env'
import { clamp01 } from '../core/math'
import type { ChangeVerb } from '../game/changeGen'
import { onLocaleChange, t } from '../i18n'

import { createElement, formatNumber, setAttr, setBarRatio, setHidden } from './dom'
import { formatClock } from './gameHud'
import { levelNameOf } from './levelText'
import { createRankingList } from './rankingList'
import { attachScrollFade } from './scrollFade'
import { isSettingsOpen } from './settings'
import './gameHud.css'

/* --- 표시 상수 ----------------------------------------------------------------- */

/** 놓친 곳 목록의 표시 상한. 12개가 전부 미발견이어도 자막판이 한 화면을 넘지 않게 한다. */
const MISSED_LIST_MAX = 12

/** 첫 시도의 번호. 이 값일 때는 회차를 아예 표시하지 않는다(아래 `attemptText` 참고). */
const FIRST_ATTEMPT = 0

/**
 * 핀이 굴러 오르는 시간(ms). 점수 내역 넉 줄은 곧바로 찍히는데 핀만 굴리는 이유: 핀은 이 판이 **다음 것**(봉투)에
 * 얹은 몫이라, 오르는 모습이 곧 "쌓이고 있다"는 정보다(로드맵 4절 2번). 0.6초는 눈이 숫자를 따라갈 수 있는
 * 상한이고, 그 뒤로는 큰 버튼("한 판 더")에 닿는 길을 막는 장식이다.
 */
export const PINS_ROLL_MS = 600

/** 굴림 한 칸의 간격(ms). 60fps 두세 프레임 - 이보다 잘게 쓰면 매 프레임 텍스트 쓰기가 된다. */
const PINS_ROLL_TICK_MS = 40

/* --- 뷰 타입 ------------------------------------------------------------------- */

/**
 * 수색이 끝난 이유. `gameState.ts`의 `HuntEndReason`과 문자열이 같으므로 그대로 넘기면 된다.
 * 타입을 import 하지 않고 여기 적는 이유는 이 화면이 **표시 계층**이기 때문이다 -
 * 상태머신이 사유를 하나 더 추가해도 결과 화면이 컴파일 에러로 막아서는 안 된다.
 */
export type RoundEndReason = 'all-found' | 'out-of-ammo' | 'all-out' | 'stalled' | 'timeout' | 'skipped'
/*
 * `all-out`은 여럿이의 것이다(`matchTypes.MatchEndReason` - 한 사람의 잔탄 0은 관전이고 **전원**이 관전이어야 끝난다).
 * 싱글의 `out-of-ammo`와 다른 문장이어야 한다 - "탄환 소진"은 내 이야기고 "전원 탄환 소진"은 방의 이야기다.
 */

/**
 * 놓친 변화 한 줄. `label`은 리빌이 만든 문구를 그대로 쓴다
 * (`reveal.ts`의 `RevealStep.label` - "거실의 소파" 같은 것).
 * 여기서 라벨을 다시 만들면 리빌에서 본 문구와 결과 화면의 문구가 갈라진다.
 */
export type MissedChangeView = Readonly<{
  id: string
  verb: ChangeVerb
  label: string
}>

/**
 * 결과 화면이 단계에 대해 알아야 하는 전부. 없으면(`undefined`) 자유 모드다.
 *
 * `cleared`를 **밖에서 받는다** - 여기서 `foundCount >= minFound`를 다시 계산하면
 * 판정식이 두 벌이 되고, 갈라진 순간 "클리어라고 떴는데 다음 단계가 안 열린다"가 된다.
 */
export type ResultLevelView = Readonly<{
  /**
   * 화면상의 순서(1부터). "3단계"의 3이다.
   *
   * `LevelDef.id`와 같은 값이며, 이 파일은 이미 그 전제 위에 서 있다(`?level=`에 그대로 쓴다).
   * 사전 키 `level.{order}.name`도 같은 전제를 쓴다 - `levelText.ts`의 `levelNameOf` 참고.
   */
  order: number
  /**
   * 사전에 키가 없을 때 쓸 이름. **화면에 그대로 쓰지 마라** - `levelText.levelNameOf`를 거쳐야 한다.
   * `levels.ts`가 아직 한국어 이름을 직접 들고 있어서 이 값은 언제나 한국어다.
   */
  name: string
  cleared: boolean
  /** 클리어에 필요한 발견 수. 실패 화면이 "몇 개만 더"를 말하는 근거다. */
  minFound: number
  /** 다음 단계가 있는가. 마지막 단계를 깬 사람에게 없는 버튼을 보여 주지 않는다. */
  hasNext: boolean
}>

/**
 * 이 판이 준 핀과 그 뒤의 보유량. **여는 버튼은 여기 없다** - 결과 화면의 유일한 목적은 "한 판 더"이고,
 * 봉투는 단계 선택 화면에서 연다(로드맵 6절이 결과 화면의 [봉투 열기]를 기각했다). 이 화면이 하는 것은
 * 핀 한 줄이 굴러 오르는 것과 "봉투까지 얼마"를 게이지로 말하는 것까지다.
 */
export type ResultPinsView = Readonly<{
  /** 이 판이 준 핀. 0이면 줄은 남되 흐리게 그린다(내역의 0점 줄과 같은 결정). */
  earned: number
  /** 이 판을 반영한 뒤의 보유 핀. */
  total: number
  /** 봉투 한 장 값. 게이지의 분모다. 값의 원본은 `envelopeTable.ts`이고 여기서는 받기만 한다. */
  price: number
  /** 봉투까지 모자란 핀. 0이면 열 수 있다. 판정은 `pins.spendPins`(`shortByOf`)의 것이고 여기서는 받기만 한다. */
  shortBy: number
}>

export type ResultView = Readonly<{
  /**
   * **방 시드.** 이것만으로는 판이 재현되지 않는다 - 변화에는 `attempt`가 함께 들어간다.
   *
   * 링크 줄이 사라진 뒤로 이 화면은 시드를 **그리지 않는다.** 그래도 뷰에 남겨 두는 이유는
   * 이 값이 "지금 보고 있는 판이 무엇인가"의 신원이기 때문이다. 표시를 걷어냈다고 신원까지
   * 지우면, 다시 무언가를 보여 줄 때 세션 쪽 배선부터 다시 뚫어야 한다.
   */
  seed: string
  /** 몇 번째 시도인가(0부터). 생략하면 첫 판으로 본다. 회차 꼬리표("2회차")의 근거다. */
  attempt?: number
  /**
   * 여럿이의 라운드 번호(1부터). 여럿이에는 시도 번호가 없고(변화 시드는 호스트가 라운드에서 파생한다) 라운드가 있어서,
   * 머리 태그의 회차 자리에 "N라운드"가 선다. 싱글은 생략한다.
   */
  round?: number
  foundCount: number
  changeCount: number
  /** 남은 탄환. 0으로 끝났다면 그 자체가 이 라운드의 이야기다. */
  ammoLeft: number
  /** 수색에 실제로 쓴 시간(ms). 속도 보너스의 근거이므로 결과에 보여야 납득이 된다. */
  huntElapsedMs: number
  score: number
  /**
   * 점수 내역(`hunt.ts`의 `ScoreBreakdown`에서 그대로 옮겨 온다). 없으면 총점만 보여 준다.
   *
   * ## 왜 총점만으로는 안 되는가
   *
   * 이 화면의 목적은 성적표가 아니라 **다음 판을 누를 이유**다(파일 상단). 총점 하나는
   * "잘했다/못했다"만 말하고 거기서 끝나지만, 네 줄로 갈라 두면 각 줄이 다음 판에 무엇을
   * 다르게 해 볼지를 말한다 - 콤보가 0이면 "오답을 줄여 봐라"이고, 속도가 낮으면
   * "초반에 더 훑어라"다. 특히 **콤보 기여분이 기본 점수와 섞이면 안 된다**
   * (`hunt.ts`의 `ScoreBreakdown` 주석): 섞는 순간 콤보가 얼마를 벌어 줬는지가 사라지고,
   * 그러면 콤보는 화면에서 반짝이는 장식일 뿐 전략이 되지 않는다.
   */
  breakdown?: Readonly<{
    /** 배수를 뺀 기본 점수(발견 수 × 100). */
    findPoints: number
    /** 배수가 기본 점수 위에 얹어 준 몫. 콤보를 한 번도 못 쌓았으면 0이다. */
    comboBonus: number
    /**
     * 걸기(M7)가 얹어 준 몫(`hunt.ScoreBreakdown.raisedBonus` - 선언 뒤 발견들의 한 벌 더). **0이면 줄이 없다** - 다른 줄은
     * 0이어도 흐리게 남지만(다음 판에 무엇을 다르게 할지 말한다) 걸기는 선택이라 "걸기 +0"은 조언이 아니라 채근이다.
     */
    raisedBonus: number
    speedBonus: number
    /** 무오답 전량 발견 보너스. 아니면 0. */
    perfectBonus: number
    /** 이 라운드의 최고 콤보. 점수가 아니라 **자랑거리**라 내역이 아니라 타일로 간다. */
    maxCombo: number
    isPerfect: boolean
  }>
  endReason: RoundEndReason
  missed: readonly MissedChangeView[]
  /** 단계 맥락. 생략하면 자유 모드의 얼굴("한 판 더")로 그린다. */
  level?: ResultLevelView
  /** 핀 줄과 봉투 게이지. 생략하면(옛 호출부 · 계정 없는 경로) 그 블록을 통째로 감춘다. */
  pins?: ResultPinsView
  /**
   * 여럿이의 순위표(로드맵 M2). 있으면 순위 자리(`.rs-ranking`)를 편다. **목록을 그리는 것은 이 화면이 아니다** -
   * `rankingList.ts`가 그 자리에 끼워 넣는다. 여기서 줄을 그리기 시작하면 두 화면 모듈이 같은 표를
   * 두 벌로 그리게 된다. 싱글은 생략한다(순위가 없는 판에 빈 표를 세우지 않는다).
   */
  ranking?: readonly ResultRankingRow[]
  /**
   * 내가 **다녀간 사람**이었던 라운드(여럿이 M5). 있으면 헤드라인이 찾는 사람의 얼굴("거의 다 봤다")이 아니라 "N개는 아무도
   * 못 찾았다"가 된다 - 다녀간 사람의 성적은 발견 수가 아니라 **아무도 못 찾은 개수**다(그것이 곧 그 사람의 점수다). 나머지
   * 화면(점수 · 순위표 · 놓친 곳)은 그대로다 - 놓친 곳 목록은 이 사람에게는 "안 들킨 곳"이라 더 읽을 맛이 난다.
   * 개수는 세션이 리빌의 놓친 목록에서 옮겨 온다(`matchSessionView.buildMatchResultView`). 여기서 세지 않는다.
   */
  visitor?: Readonly<{ unfoundCount: number }>
}>

/**
 * 순위표 한 줄의 **표시용** 모양. `matchTypes.RankingRow`를 가져오지 않는 이유는 `RoundEndReason`과 같다 -
 * 이 화면은 표시 계층이라 규칙 쪽 타입에 묶이지 않는다. 이름은 세션이 뷰에서 찾아 넣는다(id는 화면에 안 나온다).
 */
export type ResultRankingRow = Readonly<{
  name: string
  /** 이번 라운드 순위. 같은 점수는 같은 순위다. */
  rank: number
  roundScore: number
  totalScore: number
  /** 내 줄인가. 순위표에서 내 줄만 다르게 그린다. */
  isMe: boolean
  /** 내 구획에서 아무도 못 찾은 수(M11). **내 줄에만** 온다 - 이유는 `rankingList.RankingListRow.zoneUnfound`에 있다. */
  zoneUnfound?: number
}>

export type ResultHandlers = Readonly<{
  /** 새 시드로 즉시 재시작(**새 방**). 자유 모드의 측정 장치다. */
  onPlayAgain: () => void
  /**
   * 같은 방 시드로 재도전 - **방은 그대로, 변화는 새로**(시도 번호가 오른다).
   *
   * 이름의 "SameSeed"는 여전히 참이다(방 시드는 유지된다). 다만 같은 라운드가 다시 나오지는
   * 않는다 - 그 성질이 튜토리얼 재도전의 목적이고, 그래서 버튼 라벨이 "다시 하기"가 아니다.
   *
   * **선택인 이유는 여럿이다.** 매치에는 "다른 곳으로 다시"가 없다 - 다음 라운드는 방장의 `nextRound` 하나이고
   * 그것이 곧 "한 판 더"(`onPlayAgain`)다. 없으면 둘째 버튼이 사라진다. 싱글 호출부는 언제나 넘긴다.
   */
  onRetrySameSeed?: () => void
  /** 다음 단계로. 단계 모드에서 클리어했을 때만 불린다. */
  onNextLevel?: () => void
  /** 단계 선택 화면으로. 단계 모드의 유일한 출구다. */
  onExitToSelect?: () => void
}>

export type ResultScreenHandle = Readonly<{
  show: (view: ResultView) => void
  hide: () => void
  dispose: () => void
}>

export type ResultScreenOptions = Readonly<{
  mount: HTMLElement
  handlers: ResultHandlers
}>

/* --- 문구 고르기 --------------------------------------------------------------- */

/**
 * 발견 비율 → 한 줄 평의 **사전 키**.
 *
 * 위에서부터 처음 걸리는 것을 쓴다. 마지막 항목이 0인 이유는 **전멸에도 문구가 있어야** 하기 때문이다 -
 * 아무 말도 없는 결과 화면은 "졌다"만 남기고, 그건 "한 판 더"의 반대 방향이다.
 */
const HEADLINE_KEYS: readonly Readonly<{ minRatio: number; key: string }>[] = [
  { minRatio: 1, key: 'result.headline.perfect' },
  { minRatio: 0.75, key: 'result.headline.most' },
  { minRatio: 0.5, key: 'result.headline.half' },
  { minRatio: 0.25, key: 'result.headline.few' },
  { minRatio: 0, key: 'result.headline.none' },
]

/** 다녀간 사람의 헤드라인. 0이면 "전부 들켰다" - 실패에도 문구가 있다(GLOSSARY 3절). */
function visitorHeadline(unfoundCount: number): string {
  return unfoundCount === 0 ? t('result.visitor.headlineNone') : t('result.visitor.headline', { count: unfoundCount })
}

function headlineFor(foundCount: number, changeCount: number): string {
  const ratio = changeCount > 0 ? foundCount / changeCount : 0
  for (const step of HEADLINE_KEYS) {
    if (ratio >= step.minRatio) return t(step.key)
  }
  // 마지막 문턱이 0이므로 도달할 수 없다. 그래도 빈 문자열을 흘리지는 않는다.
  return t('result.headline.fallback')
}

/**
 * 단계 실패의 한 마디. **남은 개수 기준**이고, 위에서부터 처음 걸리는 것을 쓴다.
 *
 * 비율이 아니라 남은 개수로 가른 이유: "50% 찾았다"는 평가지만 "하나만 더"는 다음 행동이다.
 * 튜토리얼에서 필요한 것은 성적이 아니라 **한 번 더 누를 이유**다(9.5절).
 */
const LEVEL_FAIL_KEYS: readonly Readonly<{ maxRemaining: number; key: string }>[] = [
  { maxRemaining: 1, key: 'result.fail.one' },
  { maxRemaining: 2, key: 'result.fail.two' },
  { maxRemaining: 4, key: 'result.fail.few' },
  { maxRemaining: Number.POSITIVE_INFINITY, key: 'result.fail.many' },
]

function levelFailHeadline(remaining: number): string {
  for (const step of LEVEL_FAIL_KEYS) {
    if (remaining <= step.maxRemaining) return t(step.key)
  }
  return t('result.fail.fallback')
}

/**
 * 항목을 잇는다. 빈 것은 버린다 - 첫 판에는 회차가 없어서 `, , `가 남으면 안 된다.
 *
 * 구분자는 언제나 사전이 준다. 가운뎃점을 쓰면 자막판이 개발자 로그처럼 읽힌다(용어집 2절).
 */
function joinParts(parts: readonly string[]): string {
  return parts.filter((part) => part !== '').join(t('common.separator'))
}

/* --- DOM 헬퍼 ------------------------------------------------------------------ */

/**
 * 라벨 + 값 타일. 라벨도 언어가 바뀌면 다시 칠해야 하므로 두 노드를 함께 돌려준다.
 * `root`는 타일째 접는 자리(여럿이에만 있는 타일)가 쓴다 - 라벨의 부모를 되짚는 것보다 계약이 분명하다.
 */
type StatTile = Readonly<{ root: HTMLElement; label: HTMLElement; value: HTMLElement }>

function createStat(parent: HTMLElement, highlight = false): StatTile {
  const tile = createElement('div', highlight ? 'rs-stat is-highlight' : 'rs-stat')
  const label = createElement('span', 'rs-stat__label', '')
  // 자리표시 문구는 두지 않는다 - show()가 언제나 값을 채운 뒤에야 화면이 드러난다.
  const value = createElement('strong', 'rs-stat__value', '')
  tile.append(label, value)
  parent.append(tile)
  return { root: tile, label, value }
}

/**
 * 점수 내역 한 줄. `<dl>` 안의 이름/값 한 쌍이다.
 *
 * 줄을 **접지 않는다** - 0점인 항목도 남긴다. 0이야말로 다음 판에 손댈 곳을 말하기 때문이다
 * ("콤보 0 = 오답이 있었다", "퍼펙트 0 = 아직 못 해 봤다"). 접으면 잘한 판일수록 줄이 늘어
 * 못한 판에서만 화면이 비고, 그건 정확히 반대 방향의 피드백이다. 0은 흐리게만 그린다.
 */
type LedgerRow = Readonly<{ root: HTMLElement; label: HTMLElement; value: HTMLElement }>

function createLedgerRow(parent: HTMLElement): LedgerRow {
  const root = createElement('div', 'rs-ledger__row')
  const label = createElement('dt', 'rs-ledger__label', '')
  const value = createElement('dd', 'rs-ledger__value', '')
  root.append(label, value)
  parent.append(root)
  return { root, label, value }
}

/** `+240`. 내역은 전부 더해지는 값이라 부호를 붙여 둔다(총점과 한눈에 구별된다). */
function formatGain(value: number): string {
  const safe = Number.isFinite(value) ? Math.round(value) : 0
  return safe <= 0 ? formatNumber(safe) : `+${formatNumber(safe)}`
}

/**
 * 굴러 오르는 핀의 중간값. `progress`는 0~1의 시간 비율이다.
 *
 * 감속(1 - (1-p)²)인 이유는 마지막 몇 개가 천천히 올라야 눈이 최종값에 안착하기 때문이다. 등속이면
 * 숫자가 멎는 순간이 "끊긴 것"으로 읽힌다. 언제나 정수이고, `progress ≥ 1`이면 정확히 `earned`다.
 */
export function pinsRollValue(earned: number, progress: number): number {
  const safe = Number.isFinite(earned) ? Math.max(0, Math.floor(earned)) : 0
  const p = clamp01(Number.isFinite(progress) ? progress : 1)
  const eased = 1 - (1 - p) * (1 - p)
  return p >= 1 ? safe : Math.round(safe * eased)
}

/* --- 얼굴 (모드별 문구·버튼) ------------------------------------------------------ */

/** 버튼 하나. 라벨과 동작이 짝지어 다니므로 따로 넘기지 않는다(한쪽만 바뀌는 사고를 없앤다). */
type ResultAction = Readonly<{ label: string; run: () => void }>

/**
 * 회차 꼬리표. 첫 판(0)에는 아무것도 붙이지 않는다 -
 * 처음부터 "1회차"라고 쓰면 아직 시작하지도 않은 셈이 이미 돌아가는 것처럼 읽힌다.
 */
function attemptText(attempt: number | undefined): string {
  const value = normalizeAttempt(attempt)
  if (value <= FIRST_ATTEMPT) return ''

  // 0부터 세는 내부 값을 사람이 읽는 회차(1부터)로 옮긴다.
  return t('result.attempt', { n: value + 1 })
}

/** 시도 번호를 믿을 수 있는 정수로 좁힌다. 꼬리표와 주소가 같은 값을 봐야 둘이 어긋나지 않는다. */
function normalizeAttempt(attempt: number | undefined): number {
  const value = attempt ?? FIRST_ATTEMPT
  return Number.isSafeInteger(value) && value > FIRST_ATTEMPT ? value : FIRST_ATTEMPT
}

/**
 * 이번 결과 화면의 얼굴 전체. `show`가 이 하나만 읽어 DOM에 바른다.
 *
 * 버튼이 `null`이면 **아예 만들지 않는다**(숨기는 것이 아니라 비활성도 아니다) -
 * 마지막 단계를 깬 사람에게 회색 "다음 단계"를 보여 주는 것은 없는 길을 가리키는 일이다.
 */
type ResultFace = Readonly<{
  eyebrow: string
  headline: string
  /** 헤드라인 아래 한 줄. 자유 모드에서는 빈 문자열(요소를 감춘다). */
  goal: string
  primary: ResultAction
  secondary: ResultAction | null
  exit: ResultAction | null
  hint: string
}>

/** 단축키 안내 한 줄. 있는 버튼만 적는다 - 없는 키를 안내하면 안내가 거짓이 된다. */
function hintFor(face: Omit<ResultFace, 'hint'>): string {
  const parts = [t('result.shortcut', { key: 'Enter', label: face.primary.label })]
  if (face.secondary !== null) {
    parts.push(t('result.shortcut', { key: 'R', label: face.secondary.label }))
  }
  if (face.exit !== null) {
    parts.push(t('result.shortcut', { key: 'Esc', label: face.exit.label }))
  }
  return joinParts(parts)
}

/**
 * 자유 모드의 얼굴. 컨셉시트 8절의 측정 장치("한 판 더" = **새 방**)가 가장 큰 버튼이다.
 *
 * 부가 버튼은 방을 고정한 채 이상한 곳만 바꾼다. 수치 튜닝에서 필요한 것이 정확히 이 통제 조건이다 -
 * 방까지 함께 바뀌면 "탄환 6발이 빠듯한가"를 방 탓과 구분할 수 없다.
 */
function freeFace(view: ResultView, handlers: ResultHandlers): Omit<ResultFace, 'hint'> {
  return {
    eyebrow: joinParts([
      // 다녀간 사람의 라운드는 그 사실을 머리 태그가 먼저 말한다 - 아래 헤드라인의 주어가 "아무도"인 이유가 그것이다.
      view.visitor === undefined ? '' : t('party.visitor'),
      t('result.eyebrow'),
      t(`result.end.${view.endReason}`),
      // 여럿이는 회차가 아니라 라운드다. 둘이 같이 오는 뷰는 없다(싱글은 `round`를, 여럿이는 `attempt`를 싣지 않는다).
      view.round === undefined ? attemptText(view.attempt) : t('match.round', { round: view.round }),
    ]),
    headline: view.visitor === undefined ? headlineFor(view.foundCount, view.changeCount) : visitorHeadline(view.visitor.unfoundCount),
    goal: '',
    primary: { label: t('result.playAgain'), run: handlers.onPlayAgain },
    secondary: retryAction(handlers, 'result.retryFree'),
    exit: exitAction(view, handlers),
  }
}

/**
 * 출구 버튼. 핸들러가 없으면(자유 모드 - 갈 곳이 없다) 버튼도 없다.
 *
 * 말이 둘이다. 단계는 "단계 고르기"(돌아갈 곳이 그 화면이다)이고, 여럿이(순위표가 있는 뷰)는 **"나가기"**다 - 여럿이의 판에서
 * 나가는 사람이 하는 것은 단계를 고르는 일이 아니라 방을 떠나는 일이고, 그 차이를 버튼이 말해야 한다(GLOSSARY 3절).
 */
function exitAction(view: ResultView, handlers: ResultHandlers): ResultAction | null {
  const run = handlers.onExitToSelect
  if (run === undefined) return null
  return { label: t(view.ranking === undefined ? 'result.exit' : 'match.leave'), run }
}

/** 재도전 버튼. 핸들러가 없으면(여럿이) 버튼도 없다 - 눌러도 아무 일도 없는 버튼은 고장으로 읽힌다. */
function retryAction(handlers: ResultHandlers, key: string): ResultAction | null {
  const run = handlers.onRetrySameSeed
  return run === undefined ? null : { label: t(key), run }
}

/**
 * 단계 모드의 얼굴.
 *
 * 클리어했으면 가장 큰 버튼이 **다음 단계**다 - 커리큘럼에서 다음 행동은 하나뿐이므로
 * 고르게 만들 이유가 없다. 마지막 단계라 다음이 없으면 그 자리를 "단계 고르기"가 이어받는다.
 *
 * 실패했으면 가장 큰 버튼은 **재도전**이고, 그 옆에 "다음"에 해당하는 것을 두지 않는다.
 * 튜토리얼에서 막힌 사람에게 필요한 것은 선택지가 아니라 한 번 더 누를 큰 버튼이다.
 *
 * 그 버튼이 주는 것은 **같은 방의 다른 이상한 곳**이다. 방이 같아야 외운 것이 쌓이고(그게 학습이다),
 * 이상한 곳이 달라야 방금 본 리빌이 정답표로 쓰이지 않는다.
 */
function levelFace(
  view: ResultView,
  level: ResultLevelView,
  handlers: ResultHandlers,
): Omit<ResultFace, 'hint'> {
  const eyebrow = joinParts([
    t('result.eyebrowLevel', { order: level.order, name: levelNameOf(level.order, level.name) }),
    attemptText(view.attempt),
  ])
  const retry = retryAction(handlers, 'result.retryLevel')
  const exit: ResultAction | null =
    handlers.onExitToSelect === undefined
      ? null
      : { label: t('result.exit'), run: handlers.onExitToSelect }
  // 단계에는 언제나 재도전이 있다(싱글 호출부가 넘긴다). 없는 경우는 타입이 열어 둔 구멍이라 "한 판 더"로 받는다.
  const again: ResultAction = retry ?? { label: t('result.playAgain'), run: handlers.onPlayAgain }

  if (!level.cleared) {
    const remaining = Math.max(level.minFound - view.foundCount, 1)
    return {
      eyebrow,
      headline: levelFailHeadline(remaining),
      goal: t('result.goalFail', {
        remaining,
        min: level.minFound,
        total: view.changeCount,
      }),
      primary: again,
      secondary: null,
      exit,
    }
  }

  const next: ResultAction | null =
    level.hasNext && handlers.onNextLevel !== undefined
      ? { label: t('result.nextLevel'), run: handlers.onNextLevel }
      : null

  return {
    eyebrow,
    headline: t('result.cleared'),
    goal: t('result.goalCleared', { min: level.minFound, found: view.foundCount }),
    // 다음 단계가 없으면 출구가 곧 다음 행동이므로 큰 버튼 자리를 넘긴다.
    primary: next ?? exit ?? again,
    secondary: retry,
    exit: next === null ? null : exit,
  }
}

function faceFor(view: ResultView, handlers: ResultHandlers): ResultFace {
  const level = view.level
  const base = level === undefined ? freeFace(view, handlers) : levelFace(view, level, handlers)
  return { ...base, hint: hintFor(base) }
}

/* --- 본체 ---------------------------------------------------------------------- */

/**
 * 결과 화면을 만들어 `mount`에 붙인다. 처음에는 **숨겨져 있다** - 라운드는 외우기 페이즈로 시작한다.
 */
export function createResultScreen(options: ResultScreenOptions): ResultScreenHandle {
  const root = createElement('div', 'rs-root')
  root.hidden = true
  root.setAttribute('role', 'dialog')
  root.setAttribute('aria-modal', 'true')
  root.setAttribute('aria-labelledby', 'rs-headline')

  const card = createElement('div', 'rs-card')
  const scroll = createElement('div', 'rs-scroll')

  /* 아래 문구는 전부 `show`가 덮는다. 비워 두는 이유는 화면이 뜨기 전의 값이 보일 일이 없어서다. */
  const eyebrow = createElement('p', 'rs-eyebrow', '')
  const headline = createElement('h1', 'rs-headline', '')
  headline.id = 'rs-headline'

  /**
   * 헤드라인 아래 한 줄. 단계 모드에서만 나오며 "몇 개만 더 찾으면 클리어"를 말한다.
   * `rs-eyebrow` 스타일을 다시 쓰는 이유는 그 자리에 필요한 것이 정확히 **작은 캡션**이기 때문이다.
   */
  const goal = createElement('p', 'rs-eyebrow', '')
  goal.hidden = true

  const score = createElement('p', 'rs-score')
  const scoreValue = createElement('strong', 'rs-score__value', '0')
  const scoreUnit = createElement('span', 'rs-score__unit', '')
  /**
   * 퍼펙트 도장. 총점 옆에 붙는다 - 이 게임에서 가장 큰 성취이고, 그것을 헤드라인이 아니라
   * 점수 줄에 붙이는 이유는 **점수의 일부**이기 때문이다(내역의 마지막 줄이 그 값을 말한다).
   */
  const perfectBadge = createElement('span', 'rs-perfect', '')
  perfectBadge.hidden = true
  score.append(scoreValue, scoreUnit, perfectBadge)

  /*
   * 점수 내역 넉 줄. 순서가 곧 이 게임의 점수 구조다 -
   * 기본(발견) → 배수가 얹은 몫(콤보) → 시간(속도) → 무오답(퍼펙트).
   */
  const ledger = createElement('dl', 'rs-ledger')
  const findRow = createLedgerRow(ledger)
  const comboRow = createLedgerRow(ledger)
  // 걸기 줄(M7)은 콤보 바로 아래다 - 둘 다 발견 위에 곱해진 몫이라 붙어 있어야 "발견 × 콤보 × 걸기"의 순서로 읽힌다.
  const raiseRow = createLedgerRow(ledger)
  const speedRow = createLedgerRow(ledger)
  const perfectRow = createLedgerRow(ledger)

  /*
   * 핀 한 줄 + 봉투 게이지. 내역(`ledger`) **밖**에 두는 이유는 둘이 다른 것이기 때문이다 - 내역은 점수의
   * 구성이고 핀은 점수와 무관한 계정 재화다. 같은 표에 넣으면 "핀도 점수에 더해지나"라는 질문이 생긴다.
   * 내역이 없는 뷰(오류 경로)에서도 핀 줄은 살아야 하므로 숨김 조건도 따로다.
   */
  const pins = createElement('section', 'rs-pins')
  pins.hidden = true
  const pinsRow = createElement('div', 'rs-pins__row')
  const pinsLabel = createElement('span', 'rs-pins__label', '')
  const pinsValue = createElement('strong', 'rs-pins__value', '')
  pinsRow.append(pinsLabel, pinsValue)
  const pinsTrack = createElement('div', 'rs-pins__track')
  pinsTrack.setAttribute('aria-hidden', 'true')
  const pinsFill = createElement('div', 'rs-pins__fill')
  pinsTrack.append(pinsFill)
  const pinsText = createElement('span', 'rs-pins__text', '')
  pins.append(pinsRow, pinsTrack, pinsText)

  const stats = createElement('div', 'rs-stats')
  const foundStat = createStat(stats, true)
  /*
   * 내 구획 미발견(여럿이 M11). "찾음" 바로 옆이다 - 이 판에서 내 점수를 만드는 것이 둘이고(내가 찾은 것 · 내 구획에서
   * 아무도 못 찾은 것) 그 둘이 나란히 서야 성적표가 한 줄로 읽힌다. 싱글에는 구획이 없으므로 타일째 접힌다.
   */
  const zoneStat = createStat(stats, true)
  const comboStat = createStat(stats)
  const ammoStat = createStat(stats)
  const timeStat = createStat(stats)

  const missed = createElement('section', 'rs-missed')
  const missedTitle = createElement('h2', 'rs-missed__title', '')
  const missedList = createElement('ul', 'rs-missed__list')
  missed.append(missedTitle, missedList)

  /*
   * 순위표 **자리**(여럿이). 뷰에 `ranking`이 있을 때만 펴진다. 목록은 `rankingList.ts`가 이 노드 안에 그린다 -
   * 여기서 줄을 만들기 시작하면 표를 두 모듈이 두 벌로 그린다(`ResultView.ranking` 주석).
   * 통계 타일 **위**에 두는 이유: 여럿이에서 사람이 먼저 보는 것은 내 점수가 아니라 "몇 등인가"다.
   */
  const ranking = createElement('section', 'rs-ranking')
  ranking.hidden = true
  const rankingList = createRankingList()
  ranking.append(rankingList.root)

  /*
   * 놓친 곳 목록이 **스크롤의 마지막**이다(그 아래에 있던 링크 줄은 사라졌다 - 파일 상단).
   * 목록이 끝나는 곳과 액션 줄의 경계선 사이는 `.rs-scroll`의 아래 여백이 벌린다.
   */
  scroll.append(eyebrow, headline, goal, score, ledger, pins, ranking, stats, missed)

  const actions = createElement('div', 'rs-actions')
  /** 가장 큰 버튼. 맥락에 따라 "한 판 더", "다음 단계", "다른 곳으로 다시"가 이 자리에 온다. */
  const primaryButton = createElement('button', 'rs-again', '')
  primaryButton.type = 'button'
  const secondaryButton = createElement('button', 'rs-retry', '')
  secondaryButton.type = 'button'
  /** 단계 모드의 출구. 자유 모드에는 갈 곳이 없으므로 그때는 감춘다. */
  const exitButton = createElement('button', 'rs-retry', '')
  exitButton.type = 'button'
  const hintLine = createElement('p', 'rs-hint', '')
  actions.append(primaryButton, secondaryButton, exitButton, hintLine)

  card.append(scroll, actions)
  root.append(card)
  options.mount.append(root)

  /*
   * 스크롤 끝 신호. 두께를 남은 거리에 비례시키는 유틸이 붙는다(`scrollFade.ts`).
   *
   * 이 화면이 그 유틸이 만들어진 이유다. 고정 마스크였을 때는 바닥까지 내려도 아래 40px이
   * 계속 흐렸고, **하필 그 자리에 있는 마지막 버튼이 반쯤 지워져 보였다.** 신호여야 할 것이
   * 목적지를 가린 셈이다. 지금은 더 볼 것이 없으면 두께가 0이 되고 마스크 자체가 꺼진다.
   */
  const stopScrollFade = attachScrollFade(scroll)

  /**
   * 마지막으로 그린 뷰. 언어 전환 때 **같은 판을** 다시 그리기 위해서만 들고 있다.
   * 도메인 상태가 아니다 - 여기서 값을 고치지도, 바깥에 내보내지도 않는다.
   */
  let lastView: ResultView | null = null

  /**
   * 마지막으로 그린 얼굴. 버튼 리스너가 핸들러를 **직접** 붙지 않고 이걸 거치는 이유는
   * 같은 버튼이 맥락마다 다른 일을 하기 때문이다 - 리스너를 갈아 끼우면 해제를 한 번만 빠뜨려도
   * "다음 단계를 눌렀는데 두 판이 시작되는" 종류의 버그가 된다.
   */
  let face: ResultFace | null = null

  function runPrimary(): void {
    face?.primary.run()
  }

  function runSecondary(): void {
    face?.secondary?.run()
  }

  function runExit(): void {
    face?.exit?.run()
  }

  /**
   * 키보드 단축키. 데스크톱에서 혼자 수십 판을 돌리는 것이 이번 단계의 작업 방식이라,
   * 매 판 마우스를 버튼까지 옮기는 비용이 그대로 시행 횟수를 깎는다.
   */
  function handleKeyDown(event: KeyboardEvent): void {
    if (root.hidden) return
    /*
     * 설정 패널이 이 화면 위에 떠 있으면 키는 전부 그쪽의 것이다. 비켜서지 않으면 Esc 한 번이
     * 패널을 닫으면서 **동시에** 단계 선택으로 나가고, 그건 한 번 누른 키가 두 가지 일을 한 것이다.
     */
    if (isSettingsOpen()) return
    if (event.altKey || event.ctrlKey || event.metaKey) return

    if (event.key === 'Enter') {
      // 버튼에 포커스가 있으면 브라우저가 이미 클릭을 만든다. 두 번 시작되지 않게 비켜준다.
      if (document.activeElement instanceof HTMLButtonElement) return
      event.preventDefault()
      runPrimary()
      return
    }

    if (event.key === 'r' || event.key === 'R') {
      if (face?.secondary == null) return
      event.preventDefault()
      runSecondary()
      return
    }

    // Esc는 단계 모드의 출구다. 자유 모드에는 나갈 곳이 없으므로 아무 일도 하지 않는다.
    if (event.key === 'Escape') {
      if (face?.exit == null) return
      event.preventDefault()
      runExit()
    }
  }

  primaryButton.addEventListener('click', runPrimary)
  secondaryButton.addEventListener('click', runSecondary)
  exitButton.addEventListener('click', runExit)
  window.addEventListener('keydown', handleKeyDown)

  /** 놓친 곳 목록. 매번 다시 만든다 - 라운드당 한 번 그려지므로 재사용 최적화가 의미 없다. */
  function renderMissed(items: readonly MissedChangeView[]): void {
    missedList.replaceChildren()

    if (items.length === 0) {
      setMissedTitle(0)
      missedList.append(createElement('li', 'rs-missed__empty', t('result.missedEmpty')))
      return
    }

    setMissedTitle(items.length)
    for (const item of items.slice(0, MISSED_LIST_MAX)) {
      const row = createElement('li', 'rs-missed__item')
      row.append(
        // 스탬프에 찍히는 것은 이상한 곳의 **종류**다. 화면에서 '동사'라는 말은 쓰지 않는다.
        createElement('span', 'rs-missed__verb', t(`verb.${item.verb}`)),
        // 라벨은 리빌이 만든 문장을 그대로 쓴다. 여기서 다시 만들면 두 화면의 말이 갈라진다.
        createElement('span', 'rs-missed__label', item.label),
      )
      missedList.append(row)
    }
  }

  /**
   * 점수 내역 넉 줄과 퍼펙트 도장.
   *
   * 내역이 없는 뷰(옛 호출부·오류 경로)에서는 **줄째 감춘다.** 0으로 채워 그리면 그건
   * "0점을 받았다"는 거짓말이고, 총점만 있는 화면은 적어도 참이다.
   */
  function paintLedger(breakdown: ResultView['breakdown']): void {
    ledger.hidden = breakdown === undefined
    perfectBadge.hidden = breakdown?.isPerfect !== true
    perfectBadge.textContent = t('result.perfectBadge')

    if (breakdown === undefined) return

    const rows: readonly Readonly<{ row: LedgerRow; key: string; value: number }>[] = [
      { row: findRow, key: 'result.score.find', value: breakdown.findPoints },
      { row: comboRow, key: 'result.score.combo', value: breakdown.comboBonus },
      { row: raiseRow, key: 'result.score.raise', value: breakdown.raisedBonus },
      { row: speedRow, key: 'result.score.speed', value: breakdown.speedBonus },
      { row: perfectRow, key: 'result.score.perfect', value: breakdown.perfectBonus },
    ]

    for (const entry of rows) {
      entry.row.label.textContent = t(entry.key)
      entry.row.value.textContent = formatGain(entry.value)
      // 0인 줄은 지우지 않고 흐리게만 둔다(`createLedgerRow` 주석).
      entry.row.root.classList.toggle('is-zero', entry.value <= 0)
    }
    // 걸기만 예외다 - 걸지 않은 판에는 줄 자체가 없다(`ResultView.breakdown.raisedBonus` 주석). 흐린 0은 채근으로 읽힌다.
    raiseRow.root.hidden = breakdown.raisedBonus <= 0
  }

  function setMissedTitle(count: number): void {
    missedTitle.textContent =
      count === 0 ? t('result.missedTitle') : t('result.missedTitleCount', { count })
  }

  /**
   * 굴림의 예약. **정리 권한은 이 타이머 하나다**(`juice.ts` 규약) - `hide`·`dispose`·다음 `show`가 걷는다.
   * 굴림이 끝나기 전에 "한 판 더"를 눌러도 다음 판의 결과 화면에 지난 판의 숫자가 이어 오르지 않는다.
   */
  let rollTimer: number | null = null

  function clearRoll(): void {
    if (rollTimer === null) return
    window.clearTimeout(rollTimer)
    rollTimer = null
  }

  function setPinsValue(value: number): void {
    pinsValue.textContent = t('pins.result.value', { count: formatNumber(value) })
  }

  /**
   * 핀 줄과 봉투 게이지. 없는 뷰에서는 블록째 감춘다(0으로 그리면 "0핀을 받았다"는 거짓말이 된다).
   *
   * 굴림은 여기서 시작한다. 언어 전환으로 다시 칠할 때도 처음부터 굴린다 - 중간값을 기억해 두는 것보다
   * 다시 오르는 쪽이 코드가 짧고, 언어를 바꾸는 순간에 숫자가 한 번 더 오르는 것은 해가 아니다.
   */
  function paintPins(view: ResultPinsView | undefined): void {
    clearRoll()
    pins.hidden = view === undefined
    if (view === undefined) return

    const earned = Math.max(0, Math.floor(Number.isFinite(view.earned) ? view.earned : 0))
    pinsLabel.textContent = t('pins.result.label')
    pins.classList.toggle('is-zero', earned <= 0)

    // 부족분은 경제 모듈이 센 값이다(`shortBy`). 여기서 `price - total`을 적으면 결제 판정이 두 곳에 생긴다.
    const ready = view.shortBy === 0
    pinsText.textContent = ready ? t('pins.gauge.ready') : t('pins.gauge.until', { count: view.shortBy })
    pins.setAttribute('aria-label', t('pins.gauge.aria', { pins: view.total, price: view.price }))
    setBarRatio(pinsFill, view.price > 0 ? view.total / view.price : 0)
    pins.classList.toggle('is-ready', ready)

    // 움직임 줄이기, 또는 굴릴 것이 없으면 컷이다.
    if (earned === 0 || prefersReducedMotion()) {
      setPinsValue(earned)
      return
    }

    const ticks = Math.max(1, Math.ceil(PINS_ROLL_MS / PINS_ROLL_TICK_MS))
    let tick = 0
    setPinsValue(0)
    const step = (): void => {
      tick += 1
      setPinsValue(pinsRollValue(earned, tick / ticks))
      rollTimer = tick >= ticks ? null : window.setTimeout(step, PINS_ROLL_TICK_MS)
    }
    rollTimer = window.setTimeout(step, PINS_ROLL_TICK_MS)
  }

  /**
   * 자막판 전체를 한 뷰로 그린다. `show`와 언어 전환이 **같은 함수**를 쓴다 -
   * 갱신 경로를 두 벌로 두면 새 문구를 한쪽에만 넣는 일이 반드시 생긴다.
   */
  function paint(view: ResultView): void {
    face = faceFor(view, options.handlers)

    eyebrow.textContent = face.eyebrow
    headline.textContent = face.headline
    goal.textContent = face.goal
    goal.hidden = face.goal === ''

    primaryButton.textContent = face.primary.label
    secondaryButton.textContent = face.secondary?.label ?? ''
    secondaryButton.hidden = face.secondary === null
    exitButton.textContent = face.exit?.label ?? ''
    exitButton.hidden = face.exit === null
    hintLine.textContent = face.hint

    scoreValue.textContent = formatNumber(view.score)
    scoreUnit.textContent = t('result.scoreUnit')
    paintLedger(view.breakdown)
    paintPins(view.pins)
    // 자리를 펴고 표는 `rankingList`에 맡긴다(`ranking` 노드 주석). 없는 뷰(싱글)에서는 자리째 접힌다.
    ranking.hidden = view.ranking === undefined
    if (view.ranking !== undefined) rankingList.render(view.ranking)

    /*
     * 내 구획 미발견. 수는 호스트가 센 `self.unfoundCount`(리듀서 `unfoundOf`)를 세션이 옮겨 온 것이다
     * (`matchSessionView.buildMatchResultView`) - 여기서 `changes`를 걸러 세면 점수의 분자가 두 벌이 된다.
     */
    zoneStat.label.textContent = t('zone.result.label')
    zoneStat.value.textContent = t('zone.result.value', { count: view.visitor?.unfoundCount ?? 0 })
    setAttr(zoneStat.value, 'aria-label', t('zone.result.rowAria', { count: view.visitor?.unfoundCount ?? 0 }))
    setHidden(zoneStat.root, view.visitor === undefined)

    foundStat.label.textContent = t('result.statFound')
    foundStat.value.textContent = t('hud.foundOf', {
      found: view.foundCount,
      total: view.changeCount,
    })
    /*
     * 최고 연속. **점수가 아니라 기록이라 내역이 아니라 타일에 둔다** -
     * `combo.ts`가 콤보 수에 상한을 두지 않은 이유가 정확히 이것이다("사람이 자랑하는 것은
     * 배수가 아니라 연속 횟수"). 배수는 3.0에서 멈춰도 이 숫자는 12까지 간다.
     */
    comboStat.label.textContent = t('result.statCombo')
    comboStat.value.textContent = t('result.comboValue', {
      count: view.breakdown?.maxCombo ?? 0,
    })
    ammoStat.label.textContent = t('result.statAmmo')
    /*
     * 단계 안내와 같은 키를 쓴다. 값의 모양("6발" / "6")이 글자 하나까지 같은 자리라
     * 키를 하나 더 만들면 언젠가 한쪽만 고쳐져 화면마다 탄환 표기가 달라진다.
     */
    ammoStat.value.textContent = t('intro.ammoValue', { count: view.ammoLeft })
    timeStat.label.textContent = t('result.statTime')
    timeStat.value.textContent = formatClock(view.huntElapsedMs)

    renderMissed(view.missed)
  }

  /**
   * 언어가 바뀌면 다시 칠한다. HUD와 달리 이 화면은 프레임마다 그려지지 않으므로
   * 여기서 다시 그리지 않으면 결과 화면만 옛 언어로 남는다.
   */
  const stopLocaleWatch = onLocaleChange(() => {
    if (lastView !== null) paint(lastView)
  })

  return {
    show(view: ResultView): void {
      lastView = view
      paint(view)

      root.hidden = false
      // 포커스를 가장 큰 버튼 위에 올려 둔다. Enter 한 번이면 다음 행동이 시작되어야 한다.
      primaryButton.focus()
    },

    hide(): void {
      // 굴림이 남아 있으면 걷는다. 접힌 화면에서 숫자가 계속 오르면 다음 `show`의 첫 프레임에 옛 값이 스친다.
      clearRoll()
      // 숨긴 화면 안에 포커스가 남으면 키보드 탐색이 보이지 않는 버튼을 계속 밟는다.
      if (root.contains(document.activeElement)) {
        primaryButton.blur()
      }
      root.hidden = true
    },

    dispose(): void {
      clearRoll()
      stopLocaleWatch()
      stopScrollFade()
      primaryButton.removeEventListener('click', runPrimary)
      secondaryButton.removeEventListener('click', runSecondary)
      exitButton.removeEventListener('click', runExit)
      window.removeEventListener('keydown', handleKeyDown)
      root.remove()
    },
  }
}
